import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { segmentIsOutsideShift } from '@/features/calendar/outsideShift';
import { statusColors, calendarColors, neutralGrey } from '@/styles/theme';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import { useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import type { CalendarServiceItem } from '@/types';
import { approvalWaitLabel, showsApprovalWaitMarker } from '@/utils/workOrderStatusChips';
import {
  actualExceedsPlanned,
  displayedActualHours,
} from '@/utils/actualTime';
import { format } from 'date-fns';
import { dateFnsLocale, formatDate } from '@/utils/locale';

interface ServiceCalendarItemProps {
  item: CalendarServiceItem;
  onClick?: () => void;
}

export function ServiceCalendarItem({ item, onClick }: ServiceCalendarItemProps) {
  const { t, i18n } = useTranslation('calendar');
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { statusColors: flowColors } = useWorkOrderFlow();
  const isTentative = item.isTentative ?? false;
  // One mechanic's back-to-back jobs on the same work order (calendarMerge).
  const mergedItems = item.mergedItems && item.mergedItems.length > 1 ? item.mergedItems : null;
  const serviceSummary = mergedItems
    ? t('mergedJobs.title', { count: mergedItems.length })
    : item.serviceName;
  // Open in the linked second window (#3744): the ring is drawn in text ink so
  // it reads against every status fill in both themes.
  const isViewed = useViewedWorkOrderId() === item.bookingId;
  // Use the design-system status colors (styles/theme.ts) as the source of
  // truth so the calendar matches the rest of the app; the tenant flow-config
  // color is only a fallback for custom statuses not in the design system.
  const solidBackground =
    statusColors[item.bookingStatus] ?? flowColors[item.bookingStatus] ?? neutralGrey;
  // Tentative bars are translucent; solid status fills use the status hex.
  const backgroundColor = isTentative
    ? `${calendarColors.unassigned}30`
    : solidBackground;
  // Light status fills (yellow/cyan/green) fail WCAG with white text — pick
  // dark or light ink from luminance, same rule as MUI palette contrastText.
  const textColor = isTentative
    ? calendarColors.tentative
    : theme.palette.getContrastText(solidBackground);
  // Paused work must read as paused at a glance so supervisors can tell it
  // apart from the active (replacement) job (#547). Keyed on the per-service
  // status too, since a single service can be paused while its work order is
  // still in_progress.
  const isPaused = !isTentative && (item.status === 'paused' || item.bookingStatus === 'paused');
  // A firm customer appointment must be tellable from queued ("during the
  // day") work at a glance (#1139): exact gets a clock icon + stronger border,
  // floating keeps the default look.
  const isExact = !isTentative && item.schedulingType === 'exact';
  const isOutsideShift = !isTentative && segmentIsOutsideShift(item);
  // Derived approval wait (#3027): a small icon beside the plate; the block
  // keeps its status colour. A legacy primary `waiting_approval` status is its
  // own badge already, so it is not marked twice.
  const approvalWaitText =
    !isTentative && showsApprovalWaitMarker({ status: item.bookingStatus, isWaitingForApproval: item.isWaitingForApproval })
      ? approvalWaitLabel(t('statusLabels.waiting_approval'), item.openApprovalCount)
      : null;
  const plannedHours = item.plannedHours ?? item.durationHours;
  const actualHours = displayedActualHours(item);
  const actualExceeds = actualExceedsPlanned(actualHours, plannedHours);
  const actualFillPercent = plannedHours > 0
    ? Math.min(100, (actualHours / plannedHours) * 100)
    : 0;
  const formattedDuration = plannedHours.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const formattedActual = actualHours.toLocaleString(i18n.language, { maximumFractionDigits: 2 });

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const pointerDownRef = useRef(false);

  const handlePointerDown = useCallback(() => {
    pointerDownRef.current = true;
    setTooltipOpen(false);
    const handleUp = () => {
      pointerDownRef.current = false;
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointerup', handleUp);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  const clock = (iso: string) => format(new Date(iso), 'HH:mm', { locale: dateFnsLocale() });

  const statusLabel = t(`statusLabels.${item.bookingStatus}` as const);

  const ariaLabel = useMemo(() => {
    const parts = [
      item.workOrderNumber,
      item.vehicleRegNumber,
      serviceSummary,
      ...(mergedItems ? mergedItems.map((job) => job.serviceName) : []),
      item.customerName,
      statusLabel,
    ].filter(Boolean);
    if (isPaused) parts.push(t('statusLabels.paused'));
    if (isTentative) parts.push(t('tentativeEntry'));
    if (!isTentative && item.schedulingType) {
      parts.push(t(`schedulingTypeLabels.${item.schedulingType}`));
    }
    if (isOutsideShift) parts.push(t('outsideShift'));
    if (approvalWaitText) parts.push(approvalWaitText);
    return parts.join(' — ');
  }, [
    item.workOrderNumber,
    item.vehicleRegNumber,
    serviceSummary,
    mergedItems,
    item.customerName,
    item.schedulingType,
    statusLabel,
    isPaused,
    isTentative,
    isOutsideShift,
    approvalWaitText,
    t,
  ]);

  const tooltipContent = isTentative ? (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.main' }}>
        {t('tentativeEntry')}
      </Typography>
      <Typography variant="caption" display="block">
        {item.vehicleRegNumber} — {item.vehicleMakeModel}
      </Typography>
      <Typography variant="caption" display="block">
        {item.customerName}
      </Typography>
      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
        {t('tentativeTooltip', { date: formatDate(item.scheduledStart) })}
      </Typography>
    </Box>
  ) : (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {serviceSummary}
      </Typography>
      {mergedItems && (
        <Box component="ul" sx={{ m: 0, mb: 0.5, pl: 2 }}>
          {mergedItems.map((job) => (
            <Typography component="li" variant="caption" display="list-item" key={job.id}>
              {t('mergedJobs.line', {
                service: job.serviceName,
                start: clock(job.scheduledStart),
                end: clock(job.scheduledEnd),
                hours: (job.plannedHours ?? job.durationHours).toLocaleString(i18n.language, { maximumFractionDigits: 2 }),
              })}
            </Typography>
          ))}
        </Box>
      )}
      {item.workOrderNumber && (
        <Typography variant="caption" display="block">
          {t('workOrderNumberDisplay', { number: item.workOrderNumber })}
        </Typography>
      )}
      <Typography variant="caption" display="block" fontWeight={600}>
        {statusLabel}
      </Typography>
      {item.isActual && !mergedItems && (
        <Typography variant="caption" display="block">
          {t('actualTimeEntry', { start: clock(item.scheduledStart), end: clock(item.scheduledEnd) })}
        </Typography>
      )}
      {isPaused && (
        <Typography variant="caption" display="block" fontWeight={700} sx={{ color: 'warning.light' }}>
          {t('statusLabels.paused')}
        </Typography>
      )}
      {approvalWaitText && (
        <Typography variant="caption" display="block" fontWeight={700}>
          {approvalWaitText}
        </Typography>
      )}
      <Typography variant="caption" display="block">
        {item.vehicleRegNumber} — {item.vehicleMakeModel}
      </Typography>
      <Typography variant="caption" display="block">
        {item.customerName}
      </Typography>
      {item.customerPhone && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PhoneIcon sx={{ fontSize: 12 }} />
          <Typography variant="caption" display="block">
            {item.customerPhone}
          </Typography>
        </Box>
      )}
      {item.mechanicName && (
        <Typography variant="caption" display="block">
          {item.mechanicName}
        </Typography>
      )}
      {item.liftName && (
        <Typography variant="caption" display="block">
          {item.liftName}
        </Typography>
      )}
      {item.customerWaits && (
        <Typography variant="caption" display="block" sx={{ color: 'info.light' }}>
          {t('customerWaiting')}
        </Typography>
      )}
      {item.schedulingType && (
        <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
          {t(`schedulingTypeLabels.${item.schedulingType}`)}
        </Typography>
      )}
      {isOutsideShift && (
        <Typography variant="caption" display="block" fontWeight={700} sx={{ color: 'warning.light' }}>
          {t('outsideShiftTooltip')}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
        {t('plannedDurationDisplay', { hours: formattedDuration })}
      </Typography>
      {actualHours > 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ opacity: 0.9, color: actualExceeds ? 'warning.light' : undefined }}
        >
          {t('actualDurationDisplay', { hours: formattedActual })}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      open={tooltipOpen}
      onOpen={() => { if (!pointerDownRef.current) setTooltipOpen(true); }}
      onClose={() => setTooltipOpen(false)}
      enterTouchDelay={isMobile ? 500 : 100}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        aria-label={ariaLabel}
        aria-current={isViewed ? 'true' : undefined}
        sx={{
          position: 'relative',
          backgroundColor,
          color: textColor,
          // 1px solid default; exact uses the same width with stronger contrast
          // ink so it still reads as “firm appointment” without a heavy frame.
          border: isExact
            ? `1px solid ${alpha(textColor, 0.7)}`
            : `1px solid ${calendarColors.itemBorder}`,
          borderRadius: 0.75,
          px: 0.75,
          py: 0,
          // Parent TimelineItem sets a fixed height; fill it so lone and
          // overlapping bars stay the same size (no minHeight stretch).
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          transition: 'transform 0.1s, box-shadow 0.1s',
          ...(isTentative && {
            border: `1px dashed ${theme.palette.warning.main}`,
            opacity: 0.8,
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 4px, ${alpha(theme.palette.warning.main, 0.08)} 4px, ${alpha(theme.palette.warning.main, 0.08)} 8px)`,
          }),
          ...(isPaused && {
            opacity: 0.92,
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 5px, ${alpha(theme.palette.common.black, 0.2)} 5px, ${alpha(theme.palette.common.black, 0.2)} 10px)`,
          }),
          ...(isOutsideShift && {
            boxShadow: `inset 3px 0 0 ${theme.palette.warning.main}`,
          }),
          ...(isViewed && {
            // Inside the bar: the timeline clips anything drawn outside it.
            outline: `2px solid ${theme.palette.text.primary}`,
            outlineOffset: -2,
          }),
          '&:hover, &:focus-visible': {
            transform: 'scale(1.02)',
            boxShadow: 2,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        {actualHours > 0 && actualFillPercent > 0 && (
          <Box
            aria-hidden
            data-testid="actual-duration-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: `${actualFillPercent}%`,
              bgcolor: actualExceeds ? 'warning.main' : 'common.black',
              opacity: 0.22,
              pointerEvents: 'none',
            }}
          />
        )}
        {isExact && (
          <AccessTimeFilledIcon sx={{ fontSize: isMobile ? 13 : 11, mr: 0.25, flexShrink: 0, color: 'inherit', position: 'relative', zIndex: 1 }} />
        )}
        {isOutsideShift && (
          <WarningAmberIcon
            data-testid="outside-shift-indicator"
            aria-hidden
            sx={{ fontSize: isMobile ? 13 : 11, mr: 0.25, flexShrink: 0, color: 'inherit', position: 'relative', zIndex: 1 }}
          />
        )}
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          sx={{
            lineHeight: 1.15,
            fontSize: isMobile ? '0.75rem' : '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: 0.2,
            color: 'inherit',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {item.vehicleRegNumber}
        </Typography>
        {mergedItems && (
          <Typography
            variant="caption"
            data-testid="merged-jobs-count"
            aria-hidden
            noWrap
            sx={{ ml: 0.5, fontSize: isMobile ? '0.7rem' : '0.65rem', fontWeight: 600, opacity: 0.85, color: 'inherit', position: 'relative', zIndex: 1 }}
          >
            ×{mergedItems.length}
          </Typography>
        )}
        {approvalWaitText && (
          <PendingActionsIcon
            data-testid="approval-wait-indicator"
            aria-hidden
            sx={{ fontSize: isMobile ? 13 : 11, ml: 0.25, flexShrink: 0, color: 'inherit', position: 'relative', zIndex: 1 }}
          />
        )}
      </Box>
    </Tooltip>
  );
}
